import React, { useEffect } from 'react';
import { UseFormReturn, useWatch, Path } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@components/ui/form';
import { Checkbox } from '@components/ui/checkbox';
import { Separator } from '@components/ui/separator';
import { FormMessageSpan } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';

interface PrivacyDisclosureStepProps {
  question: HealthQuestion;
  form: UseFormReturn<HealthQuestionsSchema>;
  errorMessage?: string | null;
  setStepErrorMessage: (msg: string | null) => void;
}

export const PrivacyDisclosureStep: React.FC<PrivacyDisclosureStepProps> = ({
  question,
  errorMessage,
  form,
  setStepErrorMessage,
}) => {
  const signatureErrorRef = React.useRef<HTMLParagraphElement | null>(null);
  const disclaimerErrorRef = React.useRef<HTMLParagraphElement | null>(null);
  const acceptedErrorRef = React.useRef<HTMLParagraphElement | null>(null);

  const signature = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.signature',
  });
  const accepted = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.accepted',
  });
  const disclaimerAccepted = useWatch({
    control: form.control,
    name: 'healthQuestions.privacyDisclosure.disclaimer',
  });

  const signatureFieldState = form.getFieldState(
    'healthQuestions.privacyDisclosure.signature' as Path<HealthQuestionsSchema>
  );
  const signatureError = signatureFieldState.error?.message as
    | string
    | undefined;

  const handleSignatureChange = (value: string) => {
    form.setValue('healthQuestions.privacyDisclosure.signature', value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.trigger('healthQuestions.privacyDisclosure.signature');
  };

  // The electronic signature is never pre-filled: the signer must type their own
  // name for the signature to be meaningful.

  useEffect(() => {
    form.trigger([
      'healthQuestions.privacyDisclosure.signature',
      'healthQuestions.privacyDisclosure.accepted',
      'healthQuestions.privacyDisclosure.disclaimer',
    ]);

    if (signature && accepted && disclaimerAccepted) {
      setStepErrorMessage(null);
    }
  }, [signature, accepted, disclaimerAccepted, form, setStepErrorMessage]);

  useEffect(() => {
    setStepErrorMessage(null);
  }, []);

  // Scroll to the first visible validation error when it appears
  useEffect(() => {
    // Defer slightly to ensure DOM elements with refs are mounted
    const t = setTimeout(() => {
      if (signatureError && signatureErrorRef.current) {
        signatureErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!signatureError && errorMessage?.includes('disclaimer') && disclaimerErrorRef.current) {
        disclaimerErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!signatureError && errorMessage && !form.getValues('healthQuestions.privacyDisclosure.accepted') && acceptedErrorRef.current) {
        acceptedErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [signatureError, errorMessage, form]);

  return (
    <section className="m-0 lg:p-6 p-0 lg:card-shadow">
      <div className="">
        <h3 className="text-lg md:text-[26px] font-bold mb-4">
          Please read the following:
        </h3>
        <h4 className="text-sm text-primary md:text-xl font-bold mb-1">
          Acknowledgement
        </h4>

        <p className="text-base mb-4">
          By proceeding, you acknowledge that the information provided on this
          form is truthful and accurate regarding the health conditions of you
          and/or your dependents. If any information is incorrect, omitted,
          misleading, or constitutes fraud, our health plan may deny coverage
          for related claims, rescind your policy, or refuse to pay associated
          medical bills. Please ensure all responses are correct to avoid
          potential coverage issues.
        </p>

        <FormField
          control={form.control}
          name="healthQuestions.privacyDisclosure.disclaimer"
          render={({ field }) => (
            <FormItem className="flex items-center space-x-2">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-base !text-black">
                Disclaimer: By checking this box, I acknowledge that I have read,
                understand, and agree to this statement.
              </FormLabel>
            </FormItem>
          )}
        />
        {!signatureError && errorMessage?.includes('disclaimer') && (
          <p ref={disclaimerErrorRef}className="mt-2 text-left">
            <FormMessageSpan
              text={errorMessage}
            />
          </p>
        )}
      </div>
      <Separator className="mt-4 mb-7" />
      <h4 className="text-lg md:text-xl font-bold mb-2">Authorization</h4>
      <section className="rounded-lg mb-4">
        <p className="text-[15px]">
          Submitting this application is NOT an automatic enrollment. This is a
          request to receive an underwritten quote.
        </p>
      </section>

      <h4 className="text-lg md:text-xl font-bold mt-4 mb-2">PHI Disclosure</h4>
      <section className="rounded-lg mb-4">
        <p className="text-[15px]">
          By signing this application, I certify that the statements are true and
          correct to the best of my knowledge. I understand the following: If any
          information submitted on this form constitutes fraud or there is an
          intentional misrepresentation of the material fact, the plan may
          rescind healthcare coverage. In any such case, I understand that the
          plan will return any contributions that have previously been paid as to
          the rescinded coverage, minus claims paid. I understand that this form
          is used for information purposes only and does not bind coverage. I
          understand that The Plan gathers this information for statistical and
          actuarial uses only and it will not be used in connection with
          decisions or actions regarding employment. If I am a resident of
          Michigan, I do not have to provide information regarding height or
          weight, and this is in compliance with the requirements for GINA. I
          have read the Client Privacy Notification provided to me in this
          application. As a prospective member, I have the right to request
          restrictions on how my protected health information is used, and that
          the Plan is not required by law to grant this request, but if the
          request is granted, the Plan is bound by this agreement. I also
          understand that I have the right to revoke this consent in writing,
          except to the extent that the Plan has already used or disclosed the
          protected health information in reliance upon my consent. I further
          understand that it is my responsibility to notify the plan of any
          health or enrollment-related changes that occur after signing this
          form, up to the effective date of coverage. By signing this PHQ, I
          acknowledge that upon approval and payment of my premium, I will
          automatically become a passive, non-voting certificate class member of
          IHA Services, LLC., a Manager Managed, LLC. This certificate of
          membership will remain in force for as long as I continue to
          participate in services or benefits offered through IHA Services, LLC.
          I further understand that while I have certificate membership in IHA
          Services, LLC., that affords me no managerial status, voting rights, or
          rights to profits or liabilities. I grant full managerial duties to the
          duly appointed managers of IHA Services, LLC., a manager-managed LLC.
          Additionally, by becoming a non-voting certificate member, I
          acknowledge that I will only have access to consulting services and
          products specifically designed for IHA Services, LLC. members. As a
          Member, I understand and agree the Plan may modify health care fees or
          can terminate based on Member's experience and/or utilization. Any such
          modification or termination must be presented to the member 30 days
          prior to the member's renewal date.
        </p>
      </section>

      <h4 className="text-lg md:text-xl font-bold mt-4 mb-2">
        Client Privacy Notification
      </h4>
      <section className="rounded-lg mb-4">
        <p className="text-[15px]">
          Thank you for completing the requested information. Any information,
          including non-public personal health information, such as name,
          address, and Social Security number, including detailed protected
          health information provided, will be used for the sole purpose of
          providing a risk assessment to the health plan that will provide a
          health care benefit quote to your employer. The Plan actuary is a
          legally contracted underwriter acting as a Business Associate to the
          Plan and is subject to certain provisions of the Health Insurance
          Portability and Accountability Act of 1996 (HIPAA) regulations. The
          Plan's actuary and underwriter will not sell, license, transmit, or
          disclose this information outside of their offices except as: a)
          necessary for them to provide the services on behalf of the Plan, b)
          expressly authorized by you, c) necessary for backup documentation
          purposes, or d) required by law.
        </p>
      </section>

      <h4 className="text-lg md:text-xl font-bold mt-4 mb-2">
        Electronic Signature (Type Full Legal Name):
      </h4>
      <div className="flex flex-col w-full max-w-130 mb-4">
        <FloatingInput
          id="signature"
          label="Full legal name"
          type="text"
          value={signature || ''}
          onChange={(e) =>
            handleSignatureChange(e.target.value.replace(/^\s+/, ''))
          }
          autoComplete="name"
          aria-invalid={!!(signatureError && errorMessage)}
          wrapperClass="max-w-auto"
          inputClass="h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]"
        />
        {signatureError && errorMessage && (
          <FormMessageSpan
            className="text-center mt-2"
            text={signatureError}
          />
        )}
      </div>

      <FormField
        control={form.control}
        name="healthQuestions.privacyDisclosure.accepted"
        render={({ field }) => (
          <FormItem className="flex items-center space-x-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  form.trigger('healthQuestions.privacyDisclosure.accepted');
                }}
              />
            </FormControl>
            <FormLabel className="text-base text-black">
              By checking the box, I acknowledge that I have read, understand,
              and agree to the statement.
            </FormLabel>
          </FormItem>
        )}
      />
      {!signatureError && errorMessage && !form.getValues('healthQuestions.privacyDisclosure.accepted') && (
        <p ref={acceptedErrorRef}className="mt-2 text-left">
          <FormMessageSpan
            text={errorMessage}
          />
        </p>
      )}
    </section>
  );
};
